/**
 * Voice search utilities for AI Prescription
 */

import React from 'react';
import { setDraftInputText } from './conversationUtils';

const VOICE_SEARCH_DELAY = 3000; // 3 seconds delay after voice stops

/**
 * Handles voice search result with countdown and auto-submit
 */
export const handleVoiceResult = (
  text: string,
  formikRef: React.MutableRefObject<any>,
  voiceSearchTimerRef: React.MutableRefObject<NodeJS.Timeout | null>,
  voiceCountdownIntervalRef: React.MutableRefObject<NodeJS.Timeout | null>,
  setVoiceSearchCountdown: (value: number | null) => void,
  onSearch: (query: string, options?: { vttContent?: string }) => void
) => {
  // Aggressive normalization: remove all leading/trailing spaces and multiple spaces
  let normalizedText = text
    .trim()                    // Remove leading and trailing spaces
    .replace(/\s+/g, ' ')      // Replace multiple spaces with single space
    .replace(/^\s+/, '')       // Remove any leading spaces (extra safety)
    .replace(/\s+$/, '');      // Remove any trailing spaces (extra safety)

  // Explicitly set the normalized text in the form field to prevent double spaces
  if (formikRef.current) {
    // Use setTimeout to ensure it overrides any value set by VoiceSearchInput component
    setTimeout(() => {
      formikRef.current.setFieldValue('prescriptionText', normalizedText);
      // Persist voice input to the same module-level store as typed input
      setDraftInputText(normalizedText);
    }, 0);
  }

  // Clear any existing timers
  if (voiceSearchTimerRef.current) {
    clearTimeout(voiceSearchTimerRef.current);
  }
  if (voiceCountdownIntervalRef.current) {
    clearInterval(voiceCountdownIntervalRef.current);
  }

  // Show countdown
  setVoiceSearchCountdown(Math.ceil(VOICE_SEARCH_DELAY / 1000));

  // Update countdown every second
  let remainingSeconds = Math.ceil(VOICE_SEARCH_DELAY / 1000);
  voiceCountdownIntervalRef.current = setInterval(() => {
    remainingSeconds -= 1;
    if (remainingSeconds > 0) {
      setVoiceSearchCountdown(remainingSeconds);
    } else {
      if (voiceCountdownIntervalRef.current) {
        clearInterval(voiceCountdownIntervalRef.current);
      }
      setVoiceSearchCountdown(null);
    }
  }, 1000);

  // Set a timer to automatically search after delay
  voiceSearchTimerRef.current = setTimeout(() => {
    if (normalizedText?.trim()) {
      onSearch(normalizedText);

      // Clear the input field and draft after search is submitted
      if (formikRef.current) {
        formikRef.current.setFieldValue('prescriptionText', '');
        setDraftInputText('');
      }
    }
    setVoiceSearchCountdown(null);
    if (voiceCountdownIntervalRef.current) {
      clearInterval(voiceCountdownIntervalRef.current);
    }
  }, VOICE_SEARCH_DELAY);
};

